// SpeciesRegeneration.jsx
// Component for regenerating existing species data

import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import db from "../../Firebase";

// React Components
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

// CSS
import "../SpeciesInfoUpload/PlantEditor.css";

const API_BASE = "https://us-central1-plants-5f51e.cloudfunctions.net";

const SpeciesRegeneration = () => {
  // Hooks
  const [existingSpecies, setExistingSpecies] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSpecies, setLoadingSpecies] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Load existing species on component mount
  useEffect(() => {
    loadExistingSpecies();
  }, []);

  const loadExistingSpecies = async () => {
    try {
      setLoadingSpecies(true);
      const speciesCollection = collection(db, "Species");
      const speciesSnapshot = await getDocs(speciesCollection);
      const speciesList = speciesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by common name or species ID for better UX
      speciesList.sort((a, b) => {
        const nameA = a.commonName || a.id;
        const nameB = b.commonName || b.id;
        return nameA.localeCompare(nameB);
      });

      setExistingSpecies(speciesList);
    } catch (err) {
      setError(`Failed to load existing species: ${err.message}`);
      console.error("Error loading species:", err);
    } finally {
      setLoadingSpecies(false);
    }
  };

  const regenerateSpecies = async () => {
    if (!selectedSpecies.trim()) {
      setError("Please select a species to regenerate");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      // Call the regenerateSpecies function
      const response = await fetch(`${API_BASE}/api/regenerateSpecies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          speciesName: selectedSpecies,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      setResult(data);

      // Refresh the species list to show updated data
      await loadExistingSpecies();
    } catch (err) {
      setError(`Failed to regenerate species: ${err.message}`);
      console.error("Error regenerating species:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedSpecies("");
    setResult(null);
    setError("");
  };

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">Species Regeneration</div>

      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
        <div style={{ marginBottom: "20px" }}>
          <p style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}>
            This tool regenerates care instructions for existing species using
            AI. The existing species data will be completely overwritten with
            new AI-generated content.
          </p>

          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Select Species to Regenerate:
          </label>

          {loadingSpecies ? (
            <div style={{ padding: "10px", textAlign: "center" }}>
              Loading existing species...
            </div>
          ) : (
            <select
              value={selectedSpecies}
              onChange={(e) => setSelectedSpecies(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                marginBottom: "10px",
              }}
            >
              <option value="">-- Select a Species --</option>
              {existingSpecies.map((species) => (
                <option key={species.id} value={species.id}>
                  {species.commonName || species.id} ({species.id})
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedSpecies && (
          <div
            style={{
              padding: "15px",
              backgroundColor: "#fff3cd",
              borderRadius: "8px",
              border: "1px solid #ffeaa7",
              marginBottom: "20px",
            }}
          >
            <h4 style={{ margin: "0 0 10px 0", color: "#856404" }}>
              ⚠️ Warning:
            </h4>
            <p style={{ margin: "0", color: "#856404" }}>
              Regenerating <strong>{selectedSpecies}</strong> will completely
              overwrite all existing care instructions, family information, and
              other AI-generated content. This action cannot be undone.
            </p>
          </div>
        )}

        <div style={{ marginBottom: "20px" }}>
          <button
            onClick={regenerateSpecies}
            disabled={loading || !selectedSpecies || loadingSpecies}
            style={{
              backgroundColor: loading ? "#ccc" : "#dc3545",
              color: "white",
              padding: "12px 24px",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "16px",
              marginRight: "10px",
            }}
          >
            {loading ? "Regenerating..." : "Regenerate Species"}
          </button>

          <button
            onClick={resetForm}
            disabled={loading}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              padding: "12px 24px",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "16px",
            }}
          >
            Reset
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "15px",
              backgroundColor: "#f8d7da",
              borderRadius: "8px",
              border: "1px solid #f5c6cb",
              marginBottom: "20px",
              color: "#721c24",
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        {result && (
          <div
            style={{
              padding: "15px",
              backgroundColor: "#d4edda",
              borderRadius: "8px",
              border: "1px solid #c3e6cb",
              marginBottom: "20px",
            }}
          >
            <h4 style={{ margin: "0 0 10px 0", color: "#155724" }}>
              ✅ Species Successfully Regenerated!
            </h4>
            <div style={{ color: "#155724" }}>
              <div>
                <strong>Species:</strong>{" "}
                {result.speciesData?.commonName || selectedSpecies}
              </div>
              <div>
                <strong>Family:</strong>{" "}
                {result.speciesData?.careInstructions?.family || "N/A"}
              </div>
              <div>
                <strong>Difficulty:</strong>{" "}
                {result.speciesData?.careInstructions?.difficulty || "N/A"}
              </div>
              <div>
                <strong>Water Cadence:</strong>{" "}
                {result.speciesData?.careInstructions?.waterCadence || "N/A"}{" "}
                days
              </div>
            </div>
          </div>
        )}

        {existingSpecies.length === 0 && !loadingSpecies && (
          <div
            style={{
              padding: "15px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
              textAlign: "center",
              color: "#6c757d",
            }}
          >
            No species found in the database.
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeciesRegeneration;
